import React, { useState } from 'react';

import classnames from 'classnames/bind';

import DESIGN from '@CONSTANTS/DESIGN.constant';

import {
    AdminSingleChapterData,
    AdminSingleChapterMeta,
} from '@INTERFACES/admin-api/chapters';

import useNewSectionModal from '@HOOKS/store/modals/useNewSectionModal';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import useChapterIntroductionTextModal from '@HOOKS/store/modals/useChapterIntroductionTextModal';
import useOrderSectionsDrawer from '@HOOKS/store/drawers/useOrderSectionsDrawer';

import FloatingPublishButton from '@PAGES/admin/edit-chapter/components/FloatingPublishButton';
import DocumentIntroductionEmptyState from '@PAGES/admin/document/components/DocumentIntroductionEmptyState';

import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import ChangeOrderButton from '@COMPONENTS/COMMON/buttons/ChangeOrderButton';
import PageHeader from '@PAGES/admin/edit-chapter/components/PageHeader';
import DocumentPreviewPage from '@COMPONENTS/SHARED/DocumentPreviewPage';
import SectionCard from '../SectionCard';

import styles from './Content.module.scss';

const cx: CX = classnames.bind(styles);

const top = DESIGN['subheader-height'] + DESIGN['admin-breadcrumbs-height'];

type Props = {
    chapterData: AdminSingleChapterData;
    chapterMeta: AdminSingleChapterMeta;
    filteredChapterData: AdminSingleChapterData;
};

function Content(props: Props) {
    const {
        chapterData, chapterMeta, filteredChapterData,
    } = props;

    const { sections } = filteredChapterData || { sections: [] };

    const [isNoIntroduction, setNoIntroduction] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [showExpandButton, setShowExpandButton] = useState<boolean>(false);
    const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());

    const { canEditDocument } = useDocumentPermissions({
        documentOwnerId: chapterData.document.owner,
        permissions: chapterMeta.permissions,
    });

    const { actions: newSectionModalActions } = useNewSectionModal(() => true);
    
    const {
        actions: chapterIntroductionTextModalActions,
    } = useChapterIntroductionTextModal(() => true);

    const {
        actions: orderSectionsActions,
    } = useOrderSectionsDrawer(() => true);

    // Check introduction text content
    const introductionTextData = chapterData.introductionText;
    const hasIntroductionText = introductionTextData?.body && introductionTextData.body.trim().length > 0;
    const introductionText = introductionTextData?.body || '';

    // Track when introduction text changes to force iframe update
    React.useEffect(() => {
        if (introductionText) {
            setLastUpdateTime(Date.now());
        }
    }, [introductionText]);

    // Check if text is long enough to need truncation
    React.useEffect(() => {
        if (hasIntroductionText) {
            // Multiple checks to ensure we catch long content
            const textLength = introductionText.replace(/<[^>]*>/g, '').trim().length;
            const wordCount = introductionText.replace(/<[^>]*>/g, '').trim().split(/\s+/).length;
            const estimatedLines = Math.ceil(textLength / 70); // More conservative estimate: 70 chars per line
            
            // Multiple conditions for showing expand button - more aggressive detection
            const shouldShowExpand = (
                estimatedLines > 2 // More than 2 lines (reduced from 3)
                || textLength > 140 // More than 140 characters (reduced from 240)
                || wordCount > 25 // More than 25 words
                || (introductionText.includes('<p>') && introductionText.split('<p>').length > 2) // Multiple paragraphs
            );
            
            // Enhanced debug logging
            console.log('🔍 Chapter introduction text analysis:', {
                textLength,
                wordCount,
                estimatedLines,
                shouldShowExpand,
                hasIntroductionText,
                rawTextSample: `${introductionText.slice(0, 200)}...`,
                textPreview: introductionText.replace(/<[^>]*>/g, '').slice(0, 100),
            });
            
            setShowExpandButton(shouldShowExpand);
        } else {
            setShowExpandButton(false);
        }
    }, [introductionText, hasIntroductionText]);

    const handleToggleExpand = () => {
        const newExpandedState = !isExpanded;
        console.log('🔄 Chapter: Toggling expand state:', { 
            from: isExpanded, 
            to: newExpandedState,
            showExpandButton 
        });
        setIsExpanded(newExpandedState);
    };

    const handleOpenIntroductionModal = () => {
        chapterIntroductionTextModalActions.open({
            chapterData,
            chapterMeta,
            canEditDocument
        });
    };

    const actionButton = (
        <FloatingPublishButton
            chapterData={chapterData}
            chapterMeta={chapterMeta}
            isIntroductionOpen
            top={top}
        />
    );

    const renderIntroductionSection = () => {
        if (!hasIntroductionText) {
            return (
                <div className="container max-constraint center">
                    <DocumentIntroductionEmptyState
                        canEdit={canEditDocument}
                        onAddIntroduction={handleOpenIntroductionModal}
                        context="chapter"
                    />
                </div>
            );
        }

        return (
            <div className="container max-constraint center">
                <div className={cx('chapter-introduction-wrapper')}>
                    <div className={cx('chapter-introduction-header')}>
                        <div className={cx('title-section')}>
                            <h2 className={cx('introduction-title')}>Chapter Introduction</h2>
                            <p className={cx('introduction-description')}>
                                This content will appear at the top of the chapter, before all sections
                            </p>
                        </div>
                        <div className={cx('header-actions')}>
                            {showExpandButton && (
                                <button
                                    type="button"
                                    className={cx('expand-button')}
                                    onClick={handleToggleExpand}
                                    aria-label={isExpanded ? 'Show less' : 'Show more'}
                                >
                                    {isExpanded ? 'Show less' : 'Show more'}
                                </button>
                            )}
                            <BasicButton
                                title="Edit"
                                variant="neutral"
                                size="small"
                                onClick={handleOpenIntroductionModal}
                            />
                        </div>
                    </div>
                    <div className={cx('chapter-introduction-content', {
                        'is-collapsed': !isExpanded && showExpandButton,
                        'is-expanded': isExpanded,
                    })}
                    >
                        <div className={cx('content-wrapper')}>
                            <DocumentPreviewPage
                                body={introductionText}
                                isLandscape={false}
                                key={`preview-${lastUpdateTime}`}
                            />
                        </div>
                        {!isExpanded && showExpandButton && (
                            <div className={cx('fade-overlay')} />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={cx('content')}>
            {/* Chapter Header */}
            <div className={cx('page-header-wrapper')}>
                <PageHeader
                    chapterData={chapterData}
                    chapterMeta={chapterMeta}
                    actionButton={actionButton}
                    isNoIntroduction={isNoIntroduction}
                    setNoIntroduction={setNoIntroduction}
                />
            </div>

            {/* Introduction Text Card */}
            {renderIntroductionSection()}

            {/* Divider */}
            <div className={cx('sections-divider')} />

            {/* Sections Content */}
            <div className="container max-constraint center">
                <div className={cx('sections-header')}>
                    <h2 className={cx('sections-title')}>Sections</h2>
                    <div className={cx('sections-actions')}>
                        {sections.length > 1 && (
                            <ChangeOrderButton
                                onClick={() => {
                                    orderSectionsActions.open({
                                        documentId: chapterData.document.id,
                                        chapterId: chapterData.id,
                                        sections: chapterData.sections,
                                        canEditDocument,
                                    });
                                }}
                            />
                        )}
                    </div>
                </div>

                <div className={cx('sections-grid')}>
                    {canEditDocument && (
                        <div className={cx('grid-item')}>
                            <div className={cx('add-section-card')}>
                                <button
                                    type="button"
                                    className={cx('add-section-btn')}
                                    onClick={() => {
                                        newSectionModalActions.open({
                                            documentId: filteredChapterData.document.id,
                                            chapterId: filteredChapterData.id,
                                            parent: null,
                                        });
                                    }}
                                >
                                    <div className={cx('btn-content')}>
                                        <div className={cx('icon-wrapper')}>
                                            <svg 
                                                className={cx('plus-icon')} 
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path 
                                                    d="M12 5V19M5 12H19" 
                                                    stroke="currentColor" 
                                                    strokeWidth="2.5" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            <div className={cx('icon-glow')} />
                                        </div>
                                        <div className={cx('text-content')}>
                                            <h3 className={cx('title')}>Create New Section</h3>
                                            <p className={cx('description')}>
                                                Add a new section to organize your content
                                            </p>
                                        </div>
                                    </div>
                                    <div className={cx('card-shine')} />
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {sections.length > 0 ? (
                        sections.map((section, index) => {
                            const key = section.id;

                            return (
                                <div
                                    key={key}
                                    className={cx('grid-item')}
                                >
                                    <SectionCard
                                        index={`${index + 1}`}
                                        chapterData={filteredChapterData}
                                        chapterMeta={chapterMeta}
                                        section={section}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div className={cx('sections-not-found')}>
                            No sections
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Content;
